import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import Head from "../components/Head";
import Foot from "../components/Foot";

const apiUrl = process.env.REACT_APP_API_URL;

function isPdfPath(filePath) {
  return !!filePath && filePath.toLowerCase().endsWith(".pdf");
}

function formatDate(v) {
  if (!v) return null;
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const [year, month, day] = v.slice(0, 10).split("-");
  return `${day} ${months[Number(month) - 1]} ${year}`;
}

function VerifyCertificate() {
  const [certificateNumber, setCertificateNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [certificate, setCertificate] = useState(null);
  const [searched, setSearched] = useState(false);

  const verify = async (number) => {
    if (!number.trim()) return;
    setLoading(true);
    setError("");
    setCertificate(null);
    setSearched(true);
    try {
      const response = await axios.get(
        `${apiUrl}/api/public/certificates/${encodeURIComponent(number.trim())}`
      );
      if (response.data.success) {
        setCertificate(response.data.certificate);
      } else {
        setError(response.data.message || "Certificate not found.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "No certificate found with this number. Please check the number and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    verify(certificateNumber);
  };

  // Links printed on certificates open this page with the number already filled in
  // (/verify-certificate?number=IFM..., /verify-certificate/IFM... or the older /verifications).
  const { number: numberParam } = useParams();
  const [searchParams] = useSearchParams();
  const linkedNumber = (numberParam || searchParams.get("number") || "").trim();
  useEffect(() => {
    if (linkedNumber) {
      setCertificateNumber(linkedNumber);
      verify(linkedNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedNumber]);

  return (
    <>
      <Head />

      {/* Start Hero */}
      <section className="common-page-hero">
        <img
          src="assets/img/bg/common-bg.png"
          className="common-hero-bg ak-bg"
          alt="..."
        />
        <div className="common-hero-content">
          <div className="common-hero-info">
            <div className="ak-section-heading ak-style-1 animation-title-content animation-style3">
              <p className="ak-section-subtitle animation-title" style={{ marginTop: 100 }}>
                Certificate Authenticity
              </p>
              <h2 className="ak-section-title animation-title">Verify Certificate</h2>
            </div>
            <p className="back-btn">
              <a href="/">Home</a> / Verify Certificate
            </p>
          </div>
        </div>
      </section>
      {/* End Hero */}

      <div className="ak-height-125 ak-height-lg-80" />
      <div className="container px-md-0">
        <div className="contact-form-content">
          <div className="contact-form maxwid">
            <div>
              <h2>Check a Certificate</h2>
              <h5 className="contact-title">
                Enter the certificate number printed on the IFM Edu Academy
                certificate to confirm it was genuinely issued by us.
              </h5>
              <div className="ak-height-40 ak-height-lg-30" />
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-inputs">
                <div className="type_1">
                  <label htmlFor="certificate_number" className="form-label">
                    Certificate Number
                  </label>
                  <input
                    type="text"
                    id="certificate_number"
                    className="csame"
                    placeholder="e.g. IFM-CERT-2026-0001"
                    value={certificateNumber}
                    onChange={(e) => setCertificateNumber(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="ak-height-30 ak-height-lg-20" />
              <button type="submit" className="common-btn border-0" disabled={loading}>
                <span>{loading ? "Verifying..." : "Verify Certificate"}</span>
                <span>
                  <svg xmlns="http://www.w3.org/2000/svg" width={15} height={15} viewBox="0 0 15 15" fill="none">
                    <path d="M1.42236 6.99728H13.089M13.089 6.99728L7.48903 1.39728M13.089 6.99728L7.48903 12.5973" stroke="#030917" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </button>
            </form>

            <div className="ak-height-40 ak-height-lg-30" />

            {error && searched && !loading && (
              <div className="alert alert-danger mb-16">
                <strong>Not Verified.</strong> {error}
              </div>
            )}

            {certificate && (
              <div className="card" style={{ border: "1px solid #d6ecdd", borderRadius: 16, padding: 24, background: "#f6faf8" }}>
                <div className="d-flex align-items-center gap-8 mb-16" style={{ color: "#198754" }}>
                  <i className="ph ph-seal-check" style={{ fontSize: 28 }} />
                  <span style={{ fontSize: 20, fontWeight: 700 }}>Certificate Verified</span>
                </div>
                <div className="row g-16 mb-20">
                  <div className="col-sm-6">
                    <div style={{ fontSize: 13, color: "#6b7280" }}>Certificate Number</div>
                    <div style={{ fontWeight: 600 }}>{certificate.certificate_number}</div>
                  </div>
                  <div className="col-sm-6">
                    <div style={{ fontSize: 13, color: "#6b7280" }}>Issued To</div>
                    <div style={{ fontWeight: 600 }}>{certificate.firstName} {certificate.lastName}</div>
                  </div>
                  {certificate.coursename && (
                    <div className="col-sm-6">
                      <div style={{ fontSize: 13, color: "#6b7280" }}>Course</div>
                      <div style={{ fontWeight: 600 }}>{certificate.coursename}</div>
                    </div>
                  )}
                  {formatDate(certificate.issued_date) && (
                    <div className="col-sm-6">
                      <div style={{ fontSize: 13, color: "#6b7280" }}>Issued Date</div>
                      <div style={{ fontWeight: 600 }}>{formatDate(certificate.issued_date)}</div>
                    </div>
                  )}
                </div>

                {isPdfPath(certificate.file_path) ? (
                  <a
                    href={`${apiUrl}/${certificate.file_path}`}
                    target="_blank"
                    rel="noreferrer"
                    className="common-btn border-0"
                    style={{ display: "inline-flex" }}
                  >
                    <span>View Certificate (PDF)</span>
                  </a>
                ) : (
                  <img
                    src={`${apiUrl}/${certificate.file_path}`}
                    alt={`Certificate ${certificate.certificate_number}`}
                    style={{ maxWidth: "100%", borderRadius: 12, border: "1px solid #e5e7eb" }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="ak-height-125 ak-height-lg-80" />

      <Foot />
    </>
  );
}

export default VerifyCertificate;
